import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DashboardClaim, Guid } from '@lsq-frontend/remote-services/enterprise/common';
import {
    Color,
    forLifeOf,
    getDynamicRouteLink,
    Interaction,
    LifecycleAware,
    TabsConfig,
    ClaimService,
    FeatureFlagService,
    getRouterLinkUrlById,
    Icons
} from '@lsq-frontend/common-ui';
import { debounceTime, distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ADMIN_BUYERS,
    ADMIN_CREATE_BUYER,
    ADMIN_MANAGE_BUYER_COMPANY,
    atlas
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { AdminBuyersService } from './admin-buyers.service';

@LifecycleAware()
@Component({
    selector: 'admin-buyers',
    styleUrls: ['./admin-buyers.component.scss'],
    template: `
        <page-header [titleText]="(buyersTabActive$ | async) ? 'Buyer Implementations' : 'Buyers'">
            <div class="buyer-controls">
                <lsq-button
                    *ngIf="(canEditBuyers$ | async) && (addBuyerFeatureFlag$ | async)"
                    (buttonClick)="navigateToCreateBuyerPage()"
                    [style.marginTop]="'1rem'"
                    [style.display]="'block'"
                >
                    Add Buyer
                </lsq-button>
            </div>
        </page-header>
        <div class="container-fluid page-container">
            <tabs [tabsConfig]="tabsConfig"></tabs>
            <card *ngIf="!(buyersTabActive$ | async)">
                <div
                    right-header-content
                    class="suppliers-filters"
                    [formGroup]="buyerFiltersFormGroup"
                >
                    <lsq-input
                        class="buyer-filter"
                        placeholder="Search Buyers"
                        formControlName="buyerPreferredName"
                        [name]="'buyerPreferredName'"
                        [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                        [group]="buyerFiltersFormGroup"
                    >
                    </lsq-input>
                    <lsq-input
                        placeholder="Search Accounts"
                        formControlName="accountName"
                        [name]="'accountName'"
                        [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                        [group]="buyerFiltersFormGroup"
                    >
                    </lsq-input>
                </div>
                <div card-body [style.overflowX]="'auto'">
                    <lsq-data-table [dataSource]="dataSource" [isPaginated]="true">
                        <lsq-column
                            [column]="{
                                header: 'Buyer Name',
                                key: 'buyerPreferredName',
                                width: '25%',
                                truncateTextCss: true,
                                sortable: true
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <div class="text-truncate" [style.maxWidth]="'35rem'">
                                    <a [routerLink]="getManageBuyerLink(row.buyerKey)">
                                        {{ row.buyerPreferredName }}
                                    </a>
                                </div>
                            </ng-template>
                        </lsq-column>
                        <lsq-column
                            [column]="{
                                header: 'Account Relationship',
                                key: 'accountName',
                                maxWidth: '35rem',
                                width: '25%',
                                truncateTextCss: true,
                                sortable: true
                            }"
                        >
                        </lsq-column>
                        <lsq-column
                            [column]="{
                                header: 'Platform',
                                key: 'platformName',
                                width: '10%',
                                sortable: true
                            }"
                        >
                        </lsq-column>
                        <lsq-column
                            [nullDataDash]="true"
                            [column]="{
                                header: 'Fee Type',
                                key: 'feeType',
                                width: '10%',
                                sortable: false
                            }"
                        >
                        </lsq-column>
                        <lsq-percent-column
                            [nullDataDash]="true"
                            [percentDigitInfo]="'1.1-4'"
                            [column]="{
                                header: 'Fee Percent',
                                key: 'feePercent',
                                width: '10%',
                                sortable: false
                            }"
                        >
                        </lsq-percent-column>
                        <lsq-currency-column
                            [isCents]="true"
                            [isNullZero]="true"
                            [column]="{
                                header: 'Credit Limit',
                                key: 'creditLimit',
                                width: '10%',
                                sortable: false
                            }"
                        >
                        </lsq-currency-column>
                        <lsq-column
                            [alignRight]="true"
                            [column]="{
                                header: 'Total Suppliers',
                                key: 'suppliersCount',
                                width: '10%',
                                sortable: false
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <ng-container *ngIf="canViewSuppliers$ | async">
                                    <a
                                        routerLink="/admin/suppliers"
                                        [queryParams]="{ buyerName: row.buyerPreferredName }"
                                    >
                                        {{ row.suppliersCount || 0 }}
                                    </a>
                                </ng-container>
                                <ng-container *ngIf="!(canViewSuppliers$ | async)">
                                    {{ row.suppliersCount }}
                                </ng-container>
                            </ng-template>
                        </lsq-column>
                    </lsq-data-table>
                </div>
            </card>
            <card *ngIf="buyersTabActive$ | async">
                <div
                    right-header-content
                    class="suppliers-filters"
                    [formGroup]="buyerImplementationsFiltersFormGroup"
                >
                    <lsq-input
                        class="buyer-filter"
                        placeholder="Search Buyers"
                        formControlName="buyerPreferredName"
                        [name]="'buyerPreferredName'"
                        [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                        [group]="buyerImplementationsFiltersFormGroup"
                    >
                    </lsq-input>
                    <lsq-input
                        placeholder="Search Accounts"
                        formControlName="accountName"
                        [name]="'accountName'"
                        [prependIcon]="[icons.FaMagnifyingGlassSolid, color.GraphicGray]"
                        [group]="buyerImplementationsFiltersFormGroup"
                    >
                    </lsq-input>
                </div>
                <div card-body [style.overflowX]="'auto'">
                    <lsq-data-table
                        [dataSource]="dataSourceBuyerImplementations"
                        [isPaginated]="true"
                        [layoutFixed]="true"
                    >
                        <lsq-column
                            [column]="{
                                header: 'Buyer Name',
                                key: 'buyerPreferredName',
                                width: '50%',
                                truncateTextCss: true,
                                sortable: true
                            }"
                        >
                            <ng-template [custom-cell] let-row="row">
                                <div class="text-truncate" [style.maxWidth]="'35rem'">
                                    <a
                                        [routerLink]="getManageBuyerLink(row.buyerKey)"
                                        [queryParams]="{ isImplementation: 'true' }"
                                    >
                                        {{ row.buyerPreferredName }}
                                    </a>
                                </div>
                            </ng-template>
                        </lsq-column>
                        <lsq-column
                            [column]="{
                                header: 'Account Relationship',
                                key: 'accountName',
                                width: '50%',
                                maxWidth: '35rem',
                                truncateTextCss: true,
                                sortable: true
                            }"
                        >
                        </lsq-column>
                        <lsq-date-column
                            [isDateTimeMultiline]="true"
                            [column]="{
                                header: 'Created At',
                                key: 'createdAt',
                                width: '30rem',
                                sortable: true
                            }"
                        >
                        </lsq-date-column>
                        <lsq-date-column
                            [isDateTimeMultiline]="true"
                            [column]="{
                                header: 'Updated At',
                                key: 'updatedAt',
                                width: '12rem',
                                sortable: true
                            }"
                        >
                        </lsq-date-column>
                    </lsq-data-table>
                </div>
            </card>
        </div>
    `
})
export class AdminBuyersComponent implements OnDestroy {
    public canEditBuyers$ = this._claimService.isOn(DashboardClaim.AdminEditBuyer);
    public canViewSuppliers$ = this._claimService.isOn(DashboardClaim.AdminViewSupplier);
    public dataSource = this._adminBuyersService.dataSource;
    public dataSourceBuyerImplementations = this._adminBuyersService.dataSourceBuyerImplementations;
    public buyerFiltersFormGroup = this._adminBuyersService.buyerFiltersFormGroup;
    public buyerImplementationsFiltersFormGroup =
        this._adminBuyersService.buyerImplementationsFiltersFormGroup;

    public readonly icons = Icons;
    public readonly color = Color;
    public showBuyerImplementationsList = false;
    public addBuyerFeatureFlag$ = this._featureFlagService.isOn(FeatureFlags.AdminCreateBuyer);
    public tabsConfig: TabsConfig[] = [
        {
            label: 'Buyers',
            routerLink: getDynamicRouteLink(getRouterLinkUrlById(atlas, ADMIN_BUYERS), {}),
            queryParams: {
                showImplementations: 'false'
            }
        },
        {
            label: 'In-Progress Buyers',
            routerLink: getDynamicRouteLink(getRouterLinkUrlById(atlas, ADMIN_BUYERS), {}),
            queryParams: {
                showImplementations: 'true'
            }
        }
    ];
    public buyersTabActive$ = this._activatedRoute.queryParams.pipe(
        map((queryParams) => queryParams.showImplementations === 'true')
    );

    constructor(
        private _claimService: ClaimService,
        private _router: Router,
        private _adminBuyersService: AdminBuyersService,
        private _featureFlagService: FeatureFlagService,
        private _activatedRoute: ActivatedRoute
    ) {
        this._activatedRoute.queryParams.subscribe((queryParams) => {
            // Set the default tab's query param by default if none selected.
            if (!queryParams.showImplementations) {
                this._router.navigate([this._router.url], {
                    queryParams: { showImplementations: false }
                });
            }
        });

        const buyerFilterValueChanges = this.buyerFiltersFormGroup.valueChanges.pipe(
            forLifeOf(this),
            distinctUntilChanged(),
            debounceTime(Interaction.SearchDebounceTimeMs),
            shareReplay(1)
        );

        const buyerImplementationsFilterValueChanges =
            this.buyerImplementationsFiltersFormGroup.valueChanges.pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                debounceTime(Interaction.SearchDebounceTimeMs),
                shareReplay(1)
            );

        buyerFilterValueChanges.subscribe((buyerFilters) => {
            this.dataSource.dispatchRequest({
                pagination: {
                    skip: 0
                },
                filter: {
                    buyerPreferredName: buyerFilters.buyerPreferredName,
                    accountName: buyerFilters.accountName
                }
            });
        });

        buyerImplementationsFilterValueChanges.subscribe((buyerFilters) => {
            this.dataSourceBuyerImplementations.dispatchRequest({
                pagination: {
                    skip: 0
                },
                filter: {
                    buyerPreferredName: buyerFilters.buyerPreferredName,
                    accountName: buyerFilters.accountName
                }
            });
        });

        this._adminBuyersService.initializeBuyersListSource();
        this._adminBuyersService.initializeBuyerImplementationsListSource();
        this.dataSource = this._adminBuyersService.dataSource;
        this.dataSourceBuyerImplementations =
            this._adminBuyersService.dataSourceBuyerImplementations;
    }

    public getManageBuyerLink(buyerKey: Guid): string {
        return getDynamicRouteLink(getRouterLinkUrlById(atlas, ADMIN_MANAGE_BUYER_COMPANY), {
            buyerKey
        });
    }

    public navigateToCreateBuyerPage(): void {
        this._router.navigateByUrl(getRouterLinkUrlById(atlas, ADMIN_CREATE_BUYER));
    }

    public ngOnDestroy(): void {}
}
